import { useState, useEffect, useCallback, useMemo } from 'react';
import { BACKEND_URL, DAY_NAMES, MONTH_FULL, timeToMins, fmt, calcPricing, localISO } from '../utils';
import { CheckoutModal } from './CheckoutModal';
import { TermsModal } from './TermsModal';
import { useLang, LanguageToggle } from '../i18n';

export function BookingCalendar({ config, bookings, onBook, onGoAdmin, onGoStaff, onConfigUpdate, isShareable, trackEvent }) {
  const { t } = useLang();
  // Use server-provided AST today, or fall back to local date
  const today = new Date();
  const todayISO = config.todayISO || localISO();

  const [selectedDate, setSelectedDate] = useState(() => todayISO);
  const [calMonth, setCalMonth] = useState(() => today.getMonth());
  const [calYear, setCalYear] = useState(() => today.getFullYear());
  const [selectedSlotId, setSelectedSlotId] = useState(null);
  const [guests, setGuests] = useState(1);
  const [showCheckout, setShowCheckout] = useState(false);
  const [availability, setAvailability] = useState({});
  const [urgencyConfig, setUrgencyConfig] = useState({});
  const [showFooterTerms, setShowFooterTerms] = useState(false);

  const fetchAvailability = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/availability`);
      if (res.ok) {
        const text = await res.text();
        let data;
        try { data = JSON.parse(text); } catch { return; }
        setAvailability(data.slots || data);
        if (data.urgencyConfig) setUrgencyConfig(data.urgencyConfig);
      }
    } catch (e) {}
  }, []);

  useEffect(() => { fetchAvailability(); }, [fetchAvailability]);
  useEffect(() => { const id = setInterval(fetchAvailability, 15000); return () => clearInterval(id); }, [fetchAvailability]);

  const slotsByDate = useMemo(() => {
    const map = {};
    if (config.slots) {
      config.slots.forEach(s => {
        if (!map[s._iso]) map[s._iso] = [];
        map[s._iso].push(s);
      });
      Object.values(map).forEach(arr => arr.sort((a, b) => timeToMins(a.time) - timeToMins(b.time)));
    }
    return map;
  }, [config.slots]);

  const nowMins = config.nowMinsAST != null ? config.nowMinsAST : (today.getHours() * 60 + today.getMinutes());
  const selectedDaySlots = useMemo(() => (slotsByDate[selectedDate] || []).filter(slot => {
    if (selectedDate > todayISO) return true;
    if (selectedDate < todayISO) return false;
    return timeToMins(slot.time) > nowMins;
  }), [slotsByDate, selectedDate, todayISO, nowMins]);
  const selectedDateObj = new Date(selectedDate + 'T12:00:00');
  const selectedDayName = DAY_NAMES[selectedDateObj.getDay()];

  const calDays = useMemo(() => {
    const firstDow = new Date(calYear, calMonth, 1).getDay();
    const daysInMonth = new Date(calYear, calMonth + 1, 0).getDate();
    const days = [];
    for (let i = 0; i < firstDow; i++) days.push(null);
    for (let d = 1; d <= daysInMonth; d++) days.push(d);
    return days;
  }, [calYear, calMonth]);

  const canGoBack = calYear > today.getFullYear() || (calYear === today.getFullYear() && calMonth > today.getMonth());
  const maxDate = new Date(today); maxDate.setDate(today.getDate() + 30);
  const canGoForward = calYear < maxDate.getFullYear() || (calYear === maxDate.getFullYear() && calMonth < maxDate.getMonth());

  const selectedSlot = useMemo(() => config.slots.find(s => s.id === selectedSlotId), [config.slots, selectedSlotId]);
  const slotAvail = selectedSlot && availability[selectedSlot.id] ? availability[selectedSlot.id] : null;
  const bookedCount = slotAvail ? slotAvail.booked : 0;
  const avail = selectedSlot ? selectedSlot.capacity - bookedCount : 0;
  const displayRemaining = slotAvail ? slotAvail.displayRemaining : (selectedSlot ? selectedSlot.capacity : 0);
  const slotIsPopular = slotAvail ? slotAvail.isPopular : false;
  const pricing = useMemo(() => selectedSlot ? calcPricing(selectedSlot.price, guests, config.taxRate, config.serviceFeeRate) : null, [selectedSlot, guests, config.taxRate, config.serviceFeeRate]);

  const handleCheckoutComplete = useCallback(async (booking) => {
    await onBook(booking);
    await fetchAvailability();
  }, [onBook, fetchAvailability]);

  const handleCheckoutClose = useCallback(() => {
    setShowCheckout(false);
    setSelectedSlotId(null);
    setGuests(1);
  }, []);

  return (
    <div data-testid="booking-page">
      <div className="lr-hdr" data-testid="main-header">
        <div>
          <h1>{config.venueName}</h1>
          <div className="tag">{config.tagline}</div>
        </div>
        {!isShareable && (
          <div style={{display:'flex',gap:8,alignItems:'center'}}>
            <LanguageToggle />
            <button className="admin-btn" data-testid="staff-button" onClick={onGoStaff}>Staff</button>
            <button className="admin-btn" data-testid="admin-button" onClick={onGoAdmin}>Admin</button>
          </div>
        )}
      </div>

      <div data-testid="book-tab">
          <div className="lr-hero">
            <h2>{t('reserveYour')}<br/><em>{t('dayPass')}</em></h2>
            <p>{t('heroSubtitle')}</p>
          </div>
          <div data-testid="day-pass-description" style={{background:'#F5F0E8',padding:'20px 24px',borderLeft:'3px solid #C8A96E',borderBottom:'0.5px solid #E8E2D8'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'baseline',marginBottom:8}}>
              <span style={{color:'#1C1814',fontSize:15,fontWeight:500}}>{t('dayPassLabel')}</span>
            </div>
            <p style={{color:'#4A4438',fontSize:13,lineHeight:1.6,margin:'0 0 10px'}}>{t('dayPassDesc')}</p>
            <p style={{color:'#8A7E6E',fontSize:12,margin:0,fontStyle:'italic'}}>{t('dayPassNote')}</p>
          </div>
          <div className="lr-cal-wrap">
            <div data-testid="mini-calendar">
              <div className="mc-header">
                <button data-testid="cal-prev" disabled={!canGoBack} onClick={() => {
                  let m = calMonth - 1, y = calYear;
                  if (m < 0) { m = 11; y--; }
                  setCalMonth(m); setCalYear(y);
                }}>&lsaquo;</button>
                <div className="mc-month">{MONTH_FULL[calMonth]} {calYear}</div>
                <button data-testid="cal-next" disabled={!canGoForward} onClick={() => {
                  let m = calMonth + 1, y = calYear;
                  if (m > 11) { m = 0; y++; }
                  setCalMonth(m); setCalYear(y);
                }}>&rsaquo;</button>
              </div>
              <div className="mc-dow">
                {['S','M','T','W','T','F','S'].map((d,i) => <span key={i}>{d}</span>)}
              </div>
              <div className="mc-grid" data-testid="calendar-grid">
                {calDays.map((day, i) => {
                  if (day === null) return <div key={`e${i}`} className="mc-day empty" />;
                  const iso = `${calYear}-${String(calMonth + 1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
                  const isPast = iso < todayISO;
                  const isSelected = iso === selectedDate;
                  const hasSlots = !!slotsByDate[iso];
                  let cls = 'mc-day';
                  if (isSelected) cls += ' selected';
                  else if (isPast) cls += ' past';
                  else if (!hasSlots) cls += ' no-slots';
                  if (iso === todayISO && !isSelected) cls += ' today';
                  return (
                    <div key={i} className={cls} data-testid={`cal-day-${iso}`}
                      onClick={() => { if (!isPast && hasSlots) { setSelectedDate(iso); setSelectedSlotId(null); setGuests(1); } }}>
                      {day}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="mc-date-header" data-testid="date-header">
              <div className="mc-dayname">{selectedDayName}</div>
              <div className="mc-datestr">{MONTH_FULL[selectedDateObj.getMonth()].toUpperCase()} {selectedDateObj.getDate()}</div>
            </div>

            <div className="mc-slots" data-testid="slots-list">
              {selectedDaySlots.length === 0 ? (
                <div className="mc-no-slots">{t('noSlots')}</div>
              ) : selectedDaySlots.map(slot => {
                const sa = availability[slot.id];
                const realRem = sa ? slot.capacity - sa.booked : slot.capacity;
                const dispRem = sa ? sa.displayRemaining : slot.capacity;
                const isPopular = sa ? sa.isPopular : false;
                const isSoldOut = realRem <= 0;
                const isSel = selectedSlotId === slot.id;

                const lowThresh = urgencyConfig.urgencyLowThreshold ?? 4;
                const critThresh = urgencyConfig.urgencyCriticalThreshold ?? 2;
                const showUrgency = urgencyConfig.urgencyEnabled !== false;
                const showPopular = urgencyConfig.popularEnabled !== false;

                let urgencyPill = null;
                if (isSoldOut) {
                  urgencyPill = <span className="mc-urgency sold-out-pill" data-testid={`urgency-${slot.id}`}>{t('soldOut')}</span>;
                } else if (showUrgency && dispRem === 1) {
                  urgencyPill = <span className="mc-urgency urgent-red pulse" data-testid={`urgency-${slot.id}`}>{t('lastSpot')}</span>;
                } else if (showUrgency && dispRem <= critThresh) {
                  urgencyPill = <span className="mc-urgency urgent-red pulse" data-testid={`urgency-${slot.id}`}>{dispRem} {t('left')}</span>;
                } else if (showUrgency && dispRem <= lowThresh) {
                  urgencyPill = <span className="mc-urgency urgent-amber" data-testid={`urgency-${slot.id}`}>{dispRem} {t('left')}</span>;
                }

                return (
                  <div key={slot.id} data-testid={`slot-${slot.id}`}
                    className={`mc-slot-row${isSel ? ' selected' : ''}${isSoldOut ? ' sold-out' : ''}`}
                    onClick={() => { if (!isSoldOut) { setSelectedSlotId(isSel ? null : slot.id); setGuests(1); if (!isSel && trackEvent) trackEvent('add_to_cart', { value: slot.price }); } }}>
                    <span className="mc-stime">{slot.time}</span>
                    {urgencyPill}
                    {!urgencyPill && !isSoldOut && showPopular && isPopular && <span className="mc-popular" data-testid={`popular-${slot.id}`}>{t('popular')}</span>}
                    <span className="mc-sprice">{fmt(slot.price)}</span>
                  </div>
                );
              })}
            </div>
          </div>
      </div>

      <div className={`lr-ckbar${selectedSlot ? ' visible' : ''}`} data-testid="checkout-bar">
        {selectedSlot && pricing && (
          <>
            <div className="ck-info">
              <div className="ck-slot">{selectedSlot.day}, {selectedSlot.date} &middot; {selectedSlot.time}</div>
              <div className="ck-total">{guests} {guests > 1 ? t('guests') : t('guest')} &middot; {fmt(pricing.total)}</div>
            </div>
            <div className="ck-guests">
              <div className="g-label">{t('guestsLabel')}</div>
              <button data-testid="guests-minus" disabled={guests <= 1} onClick={() => setGuests(g => g - 1)}>&minus;</button>
              <span className="g-count">{guests}</span>
              <button data-testid="guests-plus" disabled={guests >= avail} onClick={() => setGuests(g => g + 1)}>+</button>
            </div>
            <button className="lr-cta" data-testid="reserve-button" onClick={() => { setShowCheckout(true); if (trackEvent) trackEvent('begin_checkout', { value: pricing.total }); }}>
              {t('reserve')} &middot; {fmt(pricing.total)}
            </button>
          </>
        )}
      </div>

      {showCheckout && selectedSlot && (
        <CheckoutModal
          slot={selectedSlot}
          guests={guests}
          config={config}
          displayRemaining={displayRemaining}
          isPopular={slotIsPopular}
          urgencyConfig={urgencyConfig}
          onClose={handleCheckoutClose}
          onComplete={handleCheckoutComplete}
          trackEvent={trackEvent}
        />
      )}

      {/* Footer */}
      <footer className="lr-footer" data-testid="booking-footer">
        <div className="lr-footer-inner">
          <div className="lr-footer-copy">{t('footerCopy')}</div>
          <div className="lr-footer-links">
            <a href="/privacy" data-testid="footer-privacy-link">{t('privacyPolicy')}</a>
            <span className="lr-footer-dot">&middot;</span>
            <button type="button" className="lr-footer-link-btn" data-testid="footer-terms-link" onClick={() => setShowFooterTerms(true)}>{t('termsAndConditions')}</button>
            <span className="lr-footer-dot">&middot;</span>
            <a href="mailto:hello@lasruinas.com" data-testid="footer-email-link">hello@lasruinas.com</a>
          </div>
          <div className="lr-footer-stripe">
            <a href="https://stripe.com/docs/security" target="_blank" rel="noopener noreferrer" data-testid="footer-stripe-link">{t('paymentsSecuredBy')} &#128274;</a>
          </div>
        </div>
      </footer>
      {showFooterTerms && (
        <TermsModal
          onAgree={() => setShowFooterTerms(false)}
          onClose={() => setShowFooterTerms(false)}
        />
      )}
    </div>
  );
}
